import { Table, TableBody, TableCell, TableRow, Typography } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../context'
import { useEqipEngagementFieldsWithConcerns } from '../../queries/useEqipRecommendations'
import { FieldNameCell } from './FieldNameCell'
import { PdfLoadingIndicator } from './PdfLoadingIndicator'

type ExistingConditionsSectionProps = {
  engagementId: string
}

type TraitConfig = {
  detailKey: string
  answerNs: string
  answerKeyPrefix: string
}

const TRAIT_CONFIGS: Record<string, TraitConfig> = {
  eqipPracticesFlooding: {
    detailKey: 'ponding',
    answerNs: '@cibo/landmanager/RDEqipPracticesFloodingDetailFeature',
    answerKeyPrefix: 'option_',
  },
  eqipPracticesNaturalMoisture: {
    detailKey: 'naturalMoisture',
    answerNs: '@cibo/landmanager/BooleanRollup',
    answerKeyPrefix: '',
  },
  eqipPracticesSeeps: {
    detailKey: 'seep',
    answerNs: '@cibo/landmanager/BooleanRollup',
    answerKeyPrefix: '',
  },
  eqipPracticesSnowDrifts: {
    detailKey: 'driftedSnow',
    answerNs: '@cibo/landmanager/RDEqipPracticesSnowDriftsResourceDetailFeature',
    answerKeyPrefix: 'option_',
  },
  eqipPracticesSoilCompaction: {
    detailKey: 'soilCompaction',
    answerNs: '@cibo/landmanager/BooleanRollup',
    answerKeyPrefix: '',
  },
  eqipPracticesSoilCover: {
    detailKey: 'soilCondition',
    answerNs: '@cibo/landmanager/RDEqipPracticesSoilCoverDetailFeature',
    answerKeyPrefix: 'option_',
  },
  eqipPracticesSoilErosionClassicalGully: {
    detailKey: 'classicalGully',
    answerNs: '@cibo/landmanager/BooleanRollup',
    answerKeyPrefix: '',
  },
  eqipPracticesSoilErosionEphemeralGully: {
    detailKey: 'ephemeralGully',
    answerNs: '@cibo/landmanager/BooleanRollup',
    answerKeyPrefix: '',
  },
  eqipPracticesSoilSalinity: {
    detailKey: 'soilSalinity',
    answerNs: '@cibo/landmanager/RDEqipPracticesSoilSalinityDetailFeature',
    answerKeyPrefix: 'option_',
  },
  eqipPracticesSoilSubsidence: {
    detailKey: 'soilSubsidence',
    answerNs: '@cibo/landmanager/BooleanRollup',
    answerKeyPrefix: '',
  },
  eqipPracticesWaterTable: {
    detailKey: 'highWaterTable',
    answerNs: '@cibo/landmanager/RDEqipPracticesWaterTableDetailFeature',
    answerKeyPrefix: 'option_',
  },
  eqipPracticesIrrigationMethod: {
    detailKey: 'furrowIrrigation',
    answerNs: '@cibo/landmanager/BooleanRollup',
    answerKeyPrefix: '',
  },
  eqipPracticesIrrigationQuantity: {
    detailKey: 'irrigationQuantity',
    answerNs: '@cibo/landmanager/RDEqipPracticesIrrigationQuantityResourceDetailFeature',
    answerKeyPrefix: 'option_',
  },
}

const TRAIT_IDS = Object.keys(TRAIT_CONFIGS)

export const ExistingConditionsSection = ({ engagementId }: ExistingConditionsSectionProps) => {
  const { t, i18n } = useTranslation('@cibo/programs/EqipSelectedPracticesPDF')

  const { engagement } = useProgramEngagementContext()
  const fieldModels = useEqipEngagementFieldsWithConcerns(engagementId)

  const programYear = engagement.data?.programYear ?? new Date().getFullYear()

  // Build question-first data: for each trait, collect all fields that have an answer
  const questionGroups = useMemo(() => {
    if (!fieldModels.data?.length) return []

    return TRAIT_IDS.flatMap(traitId => {
      const config = TRAIT_CONFIGS[traitId]

      const fieldAnswers = fieldModels.data!.flatMap(field => {
        const detail = field.details.find(
          d => d.traitId === traitId && (!d.year || d.year <= programYear)
        ) as Record<string, any> | undefined

        if (!detail) return []

        const value = detail.result?.[config.detailKey] ?? detail.input?.[config.detailKey]
        if (value == null) return []

        const answerLabel = i18n.t(`${config.answerKeyPrefix}${value}`, { ns: config.answerNs })

        return [{ field, answerLabel }]
      })

      if (!fieldAnswers.length) return []

      return [{ traitId, questionText: t(`existingConditionsQuestion_${traitId}`), fieldAnswers }]
    })
  }, [fieldModels.data, programYear, t, i18n])

  if (fieldModels.isPending || !fieldModels.isFetched) return <PdfLoadingIndicator />

  if (!questionGroups.length) return <div data-section="existing-conditions" />

  return (
    <div style={{ pageBreakBefore: 'always', paddingTop: 24 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('existingConditionsTitle')}
      </Typography>

      {questionGroups.map(({ traitId, questionText, fieldAnswers }) => {
        const [firstFieldAnswer, ...remainingFieldAnswers] = fieldAnswers

        return (
          <div key={traitId} style={{ marginBottom: 32 }}>
            <Table size="small">
              <TableBody
                sx={{
                  breakInside: 'avoid',
                  pageBreakInside: 'avoid',
                }}
              >
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{ fontWeight: 'bold', bgcolor: 'action.hover', py: 0.5 }}
                  >
                    {questionText}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <FieldNameCell
                    name={firstFieldAnswer.field.name || '—'}
                    acres={firstFieldAnswer.field.acres}
                  />
                  <TableCell>{firstFieldAnswer.answerLabel}</TableCell>
                </TableRow>
              </TableBody>

              {!!remainingFieldAnswers.length && (
                <TableBody>
                  {remainingFieldAnswers.map(({ field, answerLabel }, idx) => (
                    <TableRow key={idx}>
                      <FieldNameCell name={field.name || '—'} acres={field.acres} />
                      <TableCell>{answerLabel}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </div>
        )
      })}
    </div>
  )
}
